import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { AutoAdvanceCondition } from "../components/RoutingConditionSummary";
import type { ExportTriggerPolicy, SeparationConfig, Surface } from "@capture/shared";

export interface JobTypeSummary {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  documentCategory: string;
  processingMode: string;
  documentTypeAffinity: string;
  isActive: boolean;
  /**
   * ID of the currently-published JobTypeVersion, or null if the admin hasn't
   * published a draft yet. Pickers that route documents through the pipeline
   * (upload, directory import, hot folder) should filter on this — an
   * unpublished JobType can still be listed for configuration but a batch
   * targeting it would be rejected downstream (bug #10).
   */
  currentPublishedVersionId: string | null;
  /**
   * Per-job classification mode (off | source_declared | learned). Returned by
   * the list endpoint (the route spreads the full row; no response schema strips
   * it). Used by the upload dialog to offer a declared-form picker (F-043).
   */
  classificationMode?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Number of fields defined in the current version's content (C-15).
   * Derived from JobTypeVersion.content.fields.length, not the legacy
   * fieldDefinitions table count, so it stays accurate when fields are
   * configured via the versioned Form Config editor.
   */
  fieldCount: number;
  /**
   * Number of rules defined in the current version's content (C-15).
   */
  rulesCount: number;
  /**
   * Version number of the currently-published JobTypeVersion, or null when
   * the JT has never been published. Used for the publish-state badge (C-28).
   */
  publishedVersionNumber: number | null;
  /**
   * True when both a published version and a newer draft version exist,
   * meaning the admin has saved changes since the last publish (C-28).
   */
  hasUnpublishedChanges: boolean;
  /**
   * R-167 / R-185 — per-job-type display templates. The list endpoint
   * `GET /job-types` returns these scalar columns on every item (it uses a
   * Prisma `include`, not a narrowing `select`), so consumers can detect
   * job-type-level template configuration without fetching each detail.
   * Null = inherit the tenant-level template. Optional because pre-R-167
   * API responses don't include them.
   */
  batchDisplayTemplate?: string | null;
  documentDisplayTemplate?: string | null;
  pageDisplayTemplate?: string | null;
  /**
   * Plan 4 Task 5 — ID of the JobTypeCategory this job type belongs to, or
   * null when uncategorized. The list endpoint spreads the full row so this
   * field is always present at runtime; optional here only for backward-compat
   * with pre-Plan-4 API snapshots in tests.
   */
  categoryId?: string | null;
  /** SP1 §6 — job-type surface tag; absent on standard jobs / pre-SP1 snapshots. */
  surface?: Surface;
  _count: {
    fieldDefinitions: number;
    pipelineSteps: number;
    batches: number;
  };
}

export interface JobTypeDetail {
  id: string;
  tenantId: string;
  name: string;
  /**
   * R-132 — short identifier (e.g. "NOW", "DE1545") used as the prefix of
   * structured batch display ids. Null means "derive initials from name."
   */
  shortName?: string | null;
  description: string;
  documentCategory: string;
  confidenceThresholds: Record<string, number>;
  /** R-32 P2 per-Job-Type verify threshold (0–1). null = inherit 0.95 default. */
  verificationHighlightThresholds?: { threshold?: number } | null;
  layoutConfig: Record<string, unknown>;
  processingMode: string;
  documentTypeAffinity: string;
  expectedDocumentTypes: string[];
  isActive: boolean;
  /**
   * WU-1.1 — SLA target in ms (edited as hours in the UI). Null = no SLA:
   * priority aging still runs, but slaBreached / the SLA watcher stay off.
   * Optional because pre-WU-1.1 API responses don't include it.
   */
  slaTargetMs?: number | null;
  /** WU-1.1 — priority points a pending doc gains per hour waiting in its step (default 0.5). */
  priorityAgingRate?: number;
  auditCalibrationEnabled: boolean;
  auditSampleRate: number;
  auditCalibrationUntil: string | null;
  ocrMappingProfile: string | null;
  /** When true, all keyed values in this job type are auto-uppercased. */
  forceUppercase?: boolean;
  /**
   * R-155 — tri-state per-job-type override for the auto-export gate.
   *   null  → inherit tenant default (Tenant.requireManualExportRelease)
   *   true  → always require a manual click for this job type
   *   false → always auto-fire for this job type (overrides tenant default)
   * Optional because pre-R-155 API responses don't include it.
   */
  requireManualExportRelease?: boolean | null;
  /**
   * R-274 Phase 7d — job-type-level default trigger policy.
   * Destination-level overrides take precedence; this is the fallback.
   * Optional because pre-R-274 API responses don't include it.
   */
  defaultExportTriggerPolicy?: ExportTriggerPolicy | null;
  /**
   * R-167 — per-job-type display templates. Null = inherit the
   * tenant-level template (Tenant.batchDisplayTemplate, etc.).
   * Optional because pre-R-167 API responses don't include them.
   */
  batchDisplayTemplate?: string | null;
  documentDisplayTemplate?: string | null;
  pageDisplayTemplate?: string | null;
  /**
   * R-167 — opt-in flag: when this job type exports, use the frozen
   * customDisplayLabel as the export's document-number column. Null
   * (default) keeps the pre-R-167 displayId-based behavior.
   */
  defaultDocNumberSource?: string | null;
  handwritingConfig: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  pipelineSteps: PipelineStepDTO[];
  fieldGroups: FieldGroupDTO[];
  lineItemDefinitions: LineItemDefinitionDTO[];
  /**
   * WU-10 / R-130 — True when both a published version and a newer draft
   * version exist (same derivation as the list endpoint, C-28). Drives the
   * "Form Config has unpublished changes" cross-link pill on the Job Config
   * page header. Optional because pre-WU-10 API responses don't include it —
   * client treats absence as `false`.
   */
  hasUnpublishedChanges?: boolean;
  /**
   * R-239 P4 — per-job-type re-verify valve. Controls how often a
   * would-be-skipped field is re-presented so drift can be caught.
   * Optional because pre-R-239 API responses don't include it —
   * client defaults to DEFAULT_REVERIFY_VALVE ({mode:'off'}).
   */
  reverifyValve?: import("@capture/shared").ReverifyValve | null;
  /**
   * R-247 — per-job-type classification mode for the Capture gate.
   *   "off"              → no classification gate (default)
   *   "source_declared"  → require a declared form type before extraction
   *   "learned"          → fingerprint auto-route with the supervisor correction loop
   * Optional because pre-R-247 API responses don't include it —
   * client defaults to "off".
   */
  classificationMode?: string | null;
  /**
   * R-266 6a — per-job-type cross-type routing mode for intruder documents.
   *   "off"          → no cross-type detection (default)
   *   "route"        → stop intruders in Classification for an operator
   *   "route_auto"   → auto-route confident intruders without review
   *   "reject_batch" → a confirmed intruder rejects the whole batch
   * Optional because pre-R-266 API responses don't include it —
   * client defaults to "off".
   */
  crossTypeRouting?: string | null;
  /**
   * Form Layout Variants (spec §1) — opt-in per job type. When true, forms
   * on this job type may accumulate declared FormLayoutVariant rows (each
   * with its own OCR fingerprint + field learning) instead of folding every
   * observed layout into the one baseline profile. Optional because
   * pre-feature API responses don't include it — client defaults to false,
   * which also keeps the admin Layouts tab hidden (FormConfigPage).
   */
  allowLayoutVariants?: boolean;
  /**
   * R-267 6d — declared-form verify mode (active only when classificationMode === "source_declared").
   *   "off"       → trust the declaration (default)
   *   "flag"      → stop in classify on a fingerprint disagreement
   *   "flag_auto" → auto-correct to the fingerprint form on a confident mismatch
   * Optional because pre-R-267 API responses don't include it —
   * client defaults to "off".
   */
  declaredFormVerify?: string | null;
  /**
   * R-252 — per-job-type document-split strategy.
   *   "none"          → one document per import (default)
   *   "on_occurrence" → re-segment a page stream into documents at each
   *                     "document start" page-type (needs per-page identity)
   * Optional because pre-R-252 API responses don't include it —
   * client defaults to "none".
   */
  splitStrategy?: string | null;
  /** SP1 §6 — job-type surface tag; absent on standard jobs / pre-SP1 snapshots. */
  surface?: Surface;
  /**
   * R-258 P5d — duplex scanning flag. When false, every blank page is a
   * separator candidate (simplex mode). Default true. Optional because
   * pre-R-258 API responses don't include it.
   */
  duplex?: boolean;
  /**
   * R-67 Slice 3a — blank-page separator auto-split config.
   * Null = separation off (the default).
   */
  separationConfig?: SeparationConfig | null;
  _counts: {
    batches: number;
    exportDestinations: number;
  };
}

export interface PipelineStepDTO {
  id: string;
  jobTypeId: string;
  stepType: string;
  order: number;
  label: string;
  isRequired: boolean;
  autoAdvanceCondition: AutoAdvanceCondition | null;
  allowedRoles: string[];
  /**
   * F-046 — per-step re-verification policy for the verification station's
   * attention model. null/absent = "standard" (confidence-driven, today's
   * behavior); "only_failures" | "only_unverified" narrow it.
   */
  reverifyPolicy?: string | null;
}

export interface FieldGroupDTO {
  id: string;
  jobTypeId: string;
  name: string;
  type: string;
  displayOrder: number;
  layoutColumns: number;
  isCollapsible: boolean;
  isCollapsedByDefault: boolean;
  fieldDefinitions: FieldDefinitionDTO[];
}

export interface FieldDefinitionDTO {
  id: string;
  jobTypeId: string;
  groupId: string | null;
  name: string;
  /** Canonical field identifier — matches the runtime engine's field_equals condition.field. */
  internalName: string;
  label: string;
  dataType: string;
  displayOrder: number;
  columnSpan: number;
  ocrFieldMapping: string;
  isRequired: boolean;
  defaultValue: string | null;
  conditionalVisibility: Record<string, unknown> | null;
  inputMask: string | null;
  placeholder: string | null;
  helpText: string | null;
  lookupTableId: string | null;
  lookupDisplayColumn: string | null;
  lookupReturnColumn: string | null;
  dropdownOptions: Array<{ value: string; label: string }> | null;
  verificationPolicy: string;
}

export interface LineItemDefinitionDTO {
  id: string;
  jobTypeId: string;
  groupId: string;
  name: string;
  minRows: number;
  maxRows: number;
  columns: unknown[];
}

// --- List hook ---

interface ListResponse {
  items: JobTypeSummary[];
  total: number;
}

export function useJobTypes(search?: string) {
  const [items, setItems] = useState<JobTypeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      const data = await api.get<ListResponse>(`/job-types${qs}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  return { items, total, isLoading, error, refresh: load };
}

// --- Detail hook ---

export function useJobType(id: string | undefined) {
  const [jobType, setJobType] = useState<JobTypeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (opts?: { bypassCache?: boolean }) => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      // R-60a — after a mutation the server-side `cached()` entry can be
      // stale (the initial GET's fire-and-forget tag registration can lose
      // the race against the PATCH's invalidateTag, orphaning a 120s
      // entry). An explicit post-save refresh therefore appends a unique
      // query param: `cached()` keys by path+querystring, so this is a
      // guaranteed cache MISS → authoritative read. The initial mount
      // load() takes no args and stays cacheable.
      const url = opts?.bypassCache
        ? `/job-types/${id}?_cb=${Date.now()}`
        : `/job-types/${id}`;
      const data = await api.get<JobTypeDetail>(url);
      setJobType(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // R-126 (2026-05-23) — patch local state with the response of a
  // mutation we already performed, without re-fetching. Avoids the
  // `setIsLoading(true)` flash that re-renders downstream editors
  // mid-keystroke (e.g. the Calibration tab's floor inputs) and
  // strands focus. The PATCH endpoint already returns the updated
  // row, so a follow-up GET is redundant on the save path.
  const applyLocal = useCallback((next: JobTypeDetail) => {
    setJobType(next);
  }, []);

  return { jobType, isLoading, error, refresh: load, applyLocal };
}

// --- Mutation hooks ---

export function useCreateJobType() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const create = useCallback(async (data: {
    name: string;
    description?: string;
    documentCategory?: string;
    templateId?: string;
    processingMode?: string;
    categoryId?: string;
    surface?: Surface;
  }) => {
    setIsSubmitting(true);
    try {
      return await api.post<JobTypeSummary>("/job-types", data);
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { create, isSubmitting };
}

export function useUpdateJobType(id: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = useCallback(async (data: Record<string, unknown>) => {
    setIsSubmitting(true);
    try {
      return await api.patch<JobTypeDetail>(`/job-types/${id}`, data);
    } finally {
      setIsSubmitting(false);
    }
  }, [id]);

  return { update, isSubmitting };
}

export function useDeleteJobType() {
  const remove = useCallback(async (id: string) => {
    return api.delete(`/job-types/${id}`);
  }, []);

  return { remove };
}

/**
 * E-025 — admin-only HARD delete, distinct from the soft delete above.
 * Allowed only when the job type has zero referencing batches (`_count.
 * batches === 0` — already returned by GET /job-types); the API 409s
 * otherwise. Callers should gate the affordance on both the admin
 * permission and that count before showing it.
 */
export function useHardDeleteJobType() {
  const remove = useCallback(async (id: string) => {
    return api.delete(`/job-types/${id}/permanent`);
  }, []);

  return { remove };
}

export function useCloneJobType() {
  const clone = useCallback(async (id: string, name: string) => {
    return api.post<JobTypeSummary>(`/job-types/${id}/clone`, { name });
  }, []);

  return { clone };
}

export function useActivateJobType() {
  const activate = useCallback(async (id: string) => {
    return api.post<JobTypeSummary>(`/job-types/${id}/activate`, {});
  }, []);

  return { activate };
}
